import {
  CompositeFilterDescriptor, DataSourceRequestState, FilterDescriptor, getter, isCompositeFilterDescriptor, State,
  toDataSourceRequestString,
} from '@progress/kendo-data-query';

/**
 * Column filters on the Admin grids.
 *
 * Kendo's date filter compares the picked day's midnight against the full date and time, so "is equal to 08/10/2026"
 * never matches a row stamped 08/10/2026 14:30. Before a grid's state reaches a list API or kendo's process(), every
 * date filter is widened to cover the whole picked day:
 *
 *   eq d  ->  d <= value < d+1        neq d  ->  value < d  or  value >= d+1
 *   gt d  ->  value >= d+1            gte d  ->  value >= d
 *   lt d  ->  value < d               lte d  ->  value < d+1
 *
 * The grid's own filter is never changed - only the copy that is sent or processed - so the filter menu keeps
 * showing what the user picked.
 */

type FilterNode = CompositeFilterDescriptor | FilterDescriptor;

export interface DateFilterOptions {
  /**
   * The API stores the column in UTC while the grid shows it in local time: compare against the UTC instants of
   * the picked local day instead of its local midnights.
   */
  utc?: boolean;
}

/** "dd/MM/yyyy" or "dd-MM-yyyy", optionally followed by a time - the formats some Admin APIs send dates in. */
const DAY_MONTH_YEAR = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/;

/**
 * The date in a grid value: a Date, an ISO date string, or a "dd/MM/yyyy" / "dd-MM-yyyy" string (time ignored).
 * Undefined for anything empty or unparseable.
 */
export function toDate(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value;
  }
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }
  const dayMonthYear = DAY_MONTH_YEAR.exec(value.trim());
  const date = dayMonthYear
    ? new Date(+dayMonthYear[3], +dayMonthYear[2] - 1, +dayMonthYear[1])
    : new Date(value);
  return isNaN(date.getTime()) ? undefined : date;
}

/**
 * Copies of the rows with the given fields, where present, as Dates (null when empty) - so Excel exports them as
 * dates rather than text.
 */
export function withDateFields<T extends object>(rows: T[], fields: string[]): T[] {
  return rows.map(row => {
    const copy: any = { ...row };
    for (const field of fields.filter(name => name in copy)) {
      copy[field] = toDate(copy[field]) ?? null;
    }
    return copy;
  });
}

function startOfDay(date: Date, addDays = 0): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + addDays);
}

/** A Date whose local clock reads the UTC time of `date` - what kendo then serializes for the API. */
function asUtc(date: Date): Date {
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
}

function isDateFilter(filter: FilterDescriptor): boolean {
  return filter.value instanceof Date;
}

function wholeDay(filter: FilterDescriptor, options: DateFilterOptions): FilterNode {
  const boundary = options.utc ? asUtc : (date: Date) => date;
  const dayStart = boundary(startOfDay(filter.value));
  const nextDayStart = boundary(startOfDay(filter.value, 1));
  const compare = (operator: string, value: Date): FilterDescriptor => ({ ...filter, operator, value });

  switch (filter.operator) {
    case 'eq':
      return { logic: 'and', filters: [compare('gte', dayStart), compare('lt', nextDayStart)] };
    case 'neq':
      return { logic: 'or', filters: [compare('lt', dayStart), compare('gte', nextDayStart)] };
    case 'gt':
      return compare('gte', nextDayStart);
    case 'gte':
      return compare('gte', dayStart);
    case 'lt':
      return compare('lt', dayStart);
    case 'lte':
      return compare('lt', nextDayStart);
    default:
      return filter;
  }
}

function mapFilters(filter: CompositeFilterDescriptor, map: (filter: FilterDescriptor) => FilterNode): CompositeFilterDescriptor {
  return {
    ...filter,
    filters: filter.filters.map(child => isCompositeFilterDescriptor(child) ? mapFilters(child, map) : map(child)),
  };
}

/** The grid state with every date filter widened to whole days - see the top of this file. */
export function withWholeDayDateFilters<T extends State>(state: T, options: DateFilterOptions = {}): T {
  if (!state.filter?.filters?.length) {
    return state;
  }
  return {
    ...state,
    filter: mapFilters(state.filter, filter => isDateFilter(filter) ? wholeDay(filter, options) : filter),
  };
}

/** The query string a list API is sent for a server-paged grid's state. */
export function toDataSourceQuery(state: DataSourceRequestState, options: DateFilterOptions = {}): string {
  return toDataSourceRequestString(withWholeDayDateFilters(state, options));
}

/**
 * The state to pass kendo's process() for a grid whose rows are all loaded in the browser. The rows keep their dates
 * as the API sent them (ISO strings, or "dd/MM/yyyy" for a few endpoints), which a date filter's Date can't be
 * compared with - so each date filter also reads its field through toDate().
 */
export function toLocalState<T extends State>(state: T): T {
  const widened = withWholeDayDateFilters(state);
  if (!widened.filter?.filters?.length) {
    return widened;
  }
  return {
    ...widened,
    filter: mapFilters(widened.filter, filter => {
      if (!isDateFilter(filter)) {
        return filter;
      }
      const read = typeof filter.field === 'function' ? filter.field : getter(filter.field as string, true);
      return { ...filter, field: (item: any) => toDate(read(item)) };
    }),
  };
}
